import { useMemo, useState } from 'react';
import { ArrowRight, Mail, MessageCircle, Phone } from 'lucide-react';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { useSession } from '../../app/useSession';
import { useTeamMember } from '../../app/useTeamMember';
import { EntityIdBadge } from '../../components/EntityIdBadge';
import { useToast } from '../../components/Toast';
import { cn } from '../../lib/cn';
import { errorMessage } from '../../lib/errorMessage';
import { mailtoHref, telHref, whatsappHref } from '../../lib/quickActions';
import { formatHebrewRelative } from '../../lib/relativeTime';
import { useCompanies } from '../crm/companies/companiesQueries';
import { CompanyDrawer } from '../crm/companies/CompanyDrawer';
import { useContactsByCompany } from '../crm/contacts';
import { useQuickAddTask } from '../delivery/tasks/tasksQueries';
import { INVOICE_STATUS_LABELS_HE, summarizeFinance } from '../finance/projectFinance';
import { useProjectsFinance } from '../finance/projectFinanceQueries';
import type { CompanyRow } from '../crm/companies/companies.schema';
import { formatILS, QUOTE_STATUS_LABELS_HE, type QuoteStatus, statusBadgeClass } from '../quotes/quote.schema';
import { useCustomerProfile, type CustomerActivityRow } from './customerProfileQueries';
import { CustomerProjectsTab } from './CustomerProjectsTab';
import { HealthBadge } from './HealthBadge';
import styles from './CustomerProfile.module.css';

const ACTION_LABELS: Record<string, string> = {
  'companies.created': 'נוצר לקוח',
  'companies.updated': 'עודכן לקוח',
  'companies.deleted': 'נמחק לקוח',
  'contacts.created': 'נוצר איש קשר',
  'contacts.updated': 'עודכן איש קשר',
  'contacts.deleted': 'נמחק איש קשר',
  'projects.created': 'נפתח פרויקט',
  'projects.updated': 'עודכן פרויקט',
  'projects.deleted': 'נסגר פרויקט',
  'product_lines.created': 'נוסף קו מוצר',
  'product_lines.updated': 'עודכן קו מוצר',
};

function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

type TabKey = 'overview' | 'quotes' | 'projects' | 'invoices' | 'contacts' | 'tasks' | 'activity' | 'notes';

const TABS: { id: TabKey; label: string }[] = [
  { id: 'overview', label: 'סקירה' },
  { id: 'quotes', label: 'הצעות מחיר' },
  { id: 'projects', label: 'פרויקטים' },
  { id: 'invoices', label: 'חשבוניות' },
  { id: 'contacts', label: 'אנשי קשר' },
  { id: 'tasks', label: 'משימות' },
  { id: 'activity', label: 'פעילות' },
  { id: 'notes', label: 'הערות' },
];

function isTabKey(v: string | null): v is TabKey {
  return TABS.some((t) => t.id === v);
}

function initialsFor(name: string): string {
  return name.trim().slice(0, 2).toUpperCase();
}

function ActivityFeed({ rows }: { rows: CustomerActivityRow[] }) {
  if (rows.length === 0) return <p className={styles.notice}>אין פעילות עדיין על הלקוח.</p>;
  return (
    <ul className={styles.feedList}>
      {rows.map((row) => (
        <li key={row.id} className={styles.feedRow}>
          <span className={styles.feedLabel}>{actionLabel(row.action)}</span>
          <span className={styles.feedTime}>{formatHebrewRelative(row.created_at)}</span>
        </li>
      ))}
    </ul>
  );
}

export function CustomerProfile() {
  const { id: customerId } = useParams<{ id: string }>();
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const userId = sessionObj?.user.id ?? null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;
  const toast = useToast();

  const { data: companies = [] } = useCompanies(orgId);
  const company = useMemo<CompanyRow | null>(
    () => companies.find((c) => c.id === customerId) ?? null,
    [companies, customerId],
  );
  const { data: contacts = [] } = useContactsByCompany(orgId, customerId ?? null);
  const { data: profile } = useCustomerProfile(orgId, customerId ?? null);

  // ?tab= so a link can land on a customer's projects or invoices directly.
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const tab: TabKey = isTabKey(tabParam) ? tabParam : 'overview';
  function setTab(next: TabKey) {
    const nextParams = new URLSearchParams(params);
    if (next === 'overview') nextParams.delete('tab');
    else nextParams.set('tab', next);
    setParams(nextParams, { replace: true });
  }
  const [editOpen, setEditOpen] = useState(false);

  const projects = useMemo(() => profile?.projects ?? [], [profile?.projects]);
  const projectNamesById = useMemo(() => {
    const map: Record<string, string> = {};
    projects.forEach((p) => { map[p.id] = p.name; });
    return map;
  }, [projects]);
  const projectIds = useMemo(() => projects.map((p) => p.id), [projects]);
  const { data: finance } = useProjectsFinance(orgId, projectIds);
  const quickAddTask = useQuickAddTask(orgId, userId);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskProjectId, setTaskProjectId] = useState('');

  if (!customerId) {
    return <p className={styles.notice}>לקוח לא נמצא.</p>;
  }

  if (!company) {
    return (
      <div className={styles.page}>
        <p className={styles.notice}>טוען לקוח...</p>
      </div>
    );
  }

  const primaryContact = contacts[0] ?? null;
  const wa = whatsappHref(primaryContact?.phone ?? null);
  const tel = telHref(primaryContact?.phone ?? null);
  const mailto = mailtoHref(primaryContact?.email ?? null);

  const activeProjects = profile?.kpis.activeProjects ?? 0;
  const tasks = profile?.tasks ?? [];
  const activity = profile?.activity ?? [];
  const quotes = profile?.quotes ?? [];
  const invoices = profile?.invoices ?? [];
  const lifetimeRevenue = profile?.kpis.lifetimeRevenue ?? 0;
  const book = summarizeFinance(
    projects.reduce((sum, p) => sum + p.value_amount, 0),
    finance?.invoices ?? [],
    finance?.payments ?? [],
  );

  async function addTask(event: React.FormEvent) {
    event.preventDefault();
    const title = taskTitle.trim();
    const projectId = taskProjectId || projects[0]?.id;
    if (!title || !projectId) return;
    try {
      await quickAddTask.mutateAsync({ title, project_id: projectId });
      setTaskTitle('');
      toast.success('המשימה נוספה');
    } catch (err) {
      toast.error(`לא הצלחנו להוסיף · ${errorMessage(err)}`);
    }
  }
  const health = profile?.health ?? { tone: 'unknown' as const, score: 0, reason: 'טוען...' };

  return (
    <div className={styles.page}>
      <Link to="/customers" className={styles.backLink}>
        <ArrowRight size={14} aria-hidden="true" />
        חזרה לרשימת הלקוחות
      </Link>

      <header className={styles.header}>
        <div className={styles.headerMain}>
          <div className={styles.headerTopRow}>
            <span className={cn(styles.avatar, styles[`avatar_${company.color ?? 'yellow'}`])}>
              {company.initials?.trim() ? company.initials : initialsFor(company.name)}
            </span>
            <div className={styles.headerTitle}>
              <h1>{company.name}</h1>
              <p className={styles.headerMeta}>
                <EntityIdBadge id={company.id} />
                {company.industry ? <span>· {company.industry}</span> : null}
                {company.size ? <span>· {company.size}</span> : null}
              </p>
            </div>
            <div className={styles.headerActions}>
              <HealthBadge signal={health} size="md" showReason />
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => {
                  setEditOpen(true);
                }}
              >
                ערוך פרטים
              </button>
            </div>
          </div>

          {primaryContact ? (
            <div className={styles.primaryContact}>
              <span className={styles.pcLabel}>איש קשר ראשי:</span>
              <span className={styles.pcName}>{primaryContact.full_name}</span>
              {primaryContact.role_title ? (
                <span className={styles.pcRole}>· {primaryContact.role_title}</span>
              ) : null}
              {wa !== null || mailto !== null || tel !== null ? (
                <span className={styles.pcActions}>
                  {wa ? (
                    <a
                      href={wa}
                      target="_blank"
                      rel="noreferrer noopener"
                      className={styles.iconBtn}
                      aria-label="פתח WhatsApp"
                      title="WhatsApp"
                    >
                      <MessageCircle size={14} aria-hidden="true" />
                    </a>
                  ) : null}
                  {mailto ? (
                    <a
                      href={mailto}
                      className={styles.iconBtn}
                      aria-label="שלח אימייל"
                      title="אימייל"
                    >
                      <Mail size={14} aria-hidden="true" />
                    </a>
                  ) : null}
                  {tel ? (
                    <a
                      href={tel}
                      className={styles.iconBtn}
                      aria-label="התקשר"
                      title="חיוג"
                    >
                      <Phone size={14} aria-hidden="true" />
                    </a>
                  ) : null}
                </span>
              ) : null}
            </div>
          ) : (
            <p className={styles.notice}>אין איש קשר ראשי · הוסף מתוך כרטיסיית "אנשי קשר".</p>
          )}
        </div>
      </header>

      <section className={styles.kpiStrip}>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>הכנסות מצטברות</span>
          <span className={styles.kpiValue}>
            <bdi>{formatILS(lifetimeRevenue)}</bdi>
          </span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>התקבל</span>
          <span className={styles.kpiValue}>
            <bdi>{formatILS(book.received)}</bdi>
          </span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>לגבייה</span>
          <span className={styles.kpiValue}>
            <bdi>{formatILS(book.toCollect)}</bdi>
          </span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>הצעות מאושרות</span>
          <span className={styles.kpiValue}>
            <span className="num">{String(profile?.kpis.acceptedQuoteCount ?? 0)}</span>
          </span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>פרויקטים פעילים</span>
          <span className={styles.kpiValue}>
            <span className="num">{String(activeProjects)}</span>
          </span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>פרויקטים שהושלמו</span>
          <span className={styles.kpiValue}>
            <span className="num">{String(profile?.kpis.completedProjects ?? 0)}</span>
          </span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>לקוח מאז</span>
          <span className={styles.kpiValue}>
            {company.created_at ? formatHebrewRelative(company.created_at) : '—'}
          </span>
        </div>
      </section>

      <nav className={styles.tabs} role="tablist" aria-label="כרטיסיות לקוח">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={cn(styles.tab, tab === t.id && styles.tabActive)}
            onClick={() => {
              setTab(t.id);
            }}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === 'overview' ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>סקירה</h2>
          {activity.length > 0 ? (
            <div className={styles.overviewGrid}>
              <div className={styles.overviewBlock}>
                <h3>אחרון שעדכנו</h3>
                <ActivityFeed rows={activity.slice(0, 5)} />
              </div>
              <div className={styles.overviewBlock}>
                <h3>פרויקטים פתוחים</h3>
                {projects.filter((p) => p.status === 'active').length === 0 ? (
                  <p className={styles.notice}>אין פרויקטים פעילים.</p>
                ) : (
                  <ul className={styles.feedList}>
                    {projects
                      .filter((p) => p.status === 'active')
                      .map((p) => (
                        <li key={p.id} className={styles.feedRow}>
                          <Link to={`/projects/${p.id}`} className={styles.feedLabel}>
                            {p.name}
                          </Link>
                          <span className={styles.feedTime}>
                            {p.started_at ? formatHebrewRelative(p.started_at) : '—'}
                          </span>
                        </li>
                      ))}
                  </ul>
                )}
              </div>
            </div>
          ) : (
            <p className={styles.notice}>
              אין פעילות עדיין.{' '}
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => {
                  setTab('projects');
                }}
              >
                פתח פרויקט ראשון
              </button>
            </p>
          )}
        </section>
      ) : null}

      {tab === 'contacts' ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>אנשי קשר ({contacts.length})</h2>
          {contacts.length === 0 ? (
            <p className={styles.notice}>אין אנשי קשר · הוסף מתוך כרטיסיית "פרטים" בעריכה.</p>
          ) : (
            <ul className={styles.contactList}>
              {contacts.map((c) => {
                const cwa = whatsappHref(c.phone);
                const ctel = telHref(c.phone);
                const cmail = mailtoHref(c.email);
                return (
                  <li key={c.id} className={styles.contactCard}>
                    <span className={styles.contactName}>{c.full_name}</span>
                    {c.role_title ? <span className={styles.contactRole}>{c.role_title}</span> : null}
                    <span className={styles.contactMeta}>
                      {c.email ? <bdi dir="ltr">{c.email}</bdi> : null}
                      {c.email && c.phone ? ' · ' : null}
                      {c.phone ? <bdi dir="ltr">{c.phone}</bdi> : null}
                    </span>
                    <span className={styles.contactActions}>
                      {cwa ? (
                        <a href={cwa} target="_blank" rel="noreferrer noopener" className={styles.iconBtn} aria-label="WhatsApp">
                          <MessageCircle size={12} aria-hidden="true" />
                        </a>
                      ) : null}
                      {cmail ? (
                        <a href={cmail} className={styles.iconBtn} aria-label="אימייל">
                          <Mail size={12} aria-hidden="true" />
                        </a>
                      ) : null}
                      {ctel ? (
                        <a href={ctel} className={styles.iconBtn} aria-label="חיוג">
                          <Phone size={12} aria-hidden="true" />
                        </a>
                      ) : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      ) : null}

      {tab === 'projects' && orgId ? (
        <CustomerProjectsTab
          companyId={company.id}
          companyName={company.name}
          projects={projects}
          orgId={orgId}
          userId={userId}
        />
      ) : null}

      {tab === 'quotes' ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>הצעות מחיר ({quotes.length})</h2>
          {quotes.length === 0 ? (
            <p className={styles.notice}>אין הצעות מחיר ללקוח זה. צור הצעת מחיר ראשונה בדף הצעות מחיר.</p>
          ) : (
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>מספר</th>
                  <th>סטטוס</th>
                  <th>ערך</th>
                  <th>נשלחה</th>
                  <th>אושרה</th>
                  <th>בתוקף עד</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((q) => {
                  const statusKey = q.status as QuoteStatus;
                  const label = QUOTE_STATUS_LABELS_HE[statusKey];
                  return (
                    <tr key={q.id}>
                      <td>
                        <Link to={`/quotes?id=${q.id}`} className={styles.tableLink}>
                          <code dir="ltr">{q.quote_number}</code>
                        </Link>
                      </td>
                      <td>
                        <span className={cn('badge', statusBadgeClass(q.status))}>{label}</span>
                      </td>
                      <td><bdi>{formatILS(q.value_amount)}</bdi></td>
                      <td className={styles.muted}>
                        <bdi>{q.sent_at ? format(new Date(q.sent_at), 'dd/MM/yy', { locale: he }) : '—'}</bdi>
                      </td>
                      <td className={styles.muted}>
                        <bdi>{q.accepted_at ? format(new Date(q.accepted_at), 'dd/MM/yy', { locale: he }) : '—'}</bdi>
                      </td>
                      <td className={styles.muted}>
                        <bdi>{q.valid_until ? format(new Date(q.valid_until), 'dd/MM/yy', { locale: he }) : '—'}</bdi>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      ) : null}

      {tab === 'invoices' ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>חשבוניות ({invoices.length})</h2>
          {invoices.length === 0 ? (
            <p className={styles.notice}>
              אין חשבוניות. רושמים חשבונית ותשלומים בטאב כספים של כל פרויקט.
            </p>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.dataTable}>
                <thead>
                  <tr>
                    <th>מספר</th>
                    <th>פרויקט</th>
                    <th>סטטוס</th>
                    <th>סכום</th>
                    <th>שולם</th>
                    <th>יתרה</th>
                    <th>תאריך</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => {
                    const bal = book.byInvoice[inv.id] ?? { paid: 0, due: 0 };
                    return (
                      <tr key={inv.id}>
                        <td><bdi>{inv.invoice_number}</bdi></td>
                        <td>
                          <Link to={`/projects/${inv.project_id}?tab=finance`} className={styles.tableLink}>
                            {projectNamesById[inv.project_id] ?? '—'}
                          </Link>
                        </td>
                        <td>
                          <span className="badge">
                            {(INVOICE_STATUS_LABELS_HE as Record<string, string | undefined>)[inv.status] ??
                              inv.status}
                          </span>
                        </td>
                        <td className={styles.numCell}><bdi>{formatILS(inv.amount)}</bdi></td>
                        <td className={styles.numCell}><bdi>{formatILS(bal.paid)}</bdi></td>
                        <td className={styles.numCell}><bdi>{formatILS(bal.due)}</bdi></td>
                        <td className={styles.muted}>
                          <bdi>{inv.sent_at ? format(new Date(inv.sent_at), 'dd/MM/yy', { locale: he }) : '—'}</bdi>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}

      {tab === 'tasks' ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>משימות פתוחות ({tasks.length})</h2>
          {projects.length > 0 ? (
            <form
              className={styles.inlineAdd}
              onSubmit={(e) => {
                void addTask(e);
              }}
            >
              <input
                type="text"
                className={styles.lineInput}
                aria-label="משימה חדשה"
                placeholder="הקלד משימה ולחץ Enter…"
                value={taskTitle}
                onChange={(e) => {
                  setTaskTitle(e.target.value);
                }}
              />
              <select
                className={styles.lineInput}
                aria-label="לאיזה פרויקט"
                value={taskProjectId || (projects[0]?.id ?? '')}
                onChange={(e) => {
                  setTaskProjectId(e.target.value);
                }}
              >
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <button
                type="submit"
                className={styles.btnSecondary}
                disabled={!taskTitle.trim() || quickAddTask.isPending}
              >
                הוסף משימה
              </button>
            </form>
          ) : (
            <p className={styles.notice}>משימה של לקוח שייכת לפרויקט. פתח פרויקט קודם, בטאב פרויקטים.</p>
          )}
          {tasks.length === 0 ? (
            <p className={styles.notice}>אין משימות פתוחות.</p>
          ) : (
            <ul className={styles.feedList}>
              {tasks.map((t) => (
                <li key={t.id} className={styles.feedRow}>
                  <Link
                    to={t.project_id ? `/projects/${t.project_id}?tab=tasks` : '/tasks'}
                    className={styles.feedLabel}
                  >
                    {t.title}
                  </Link>
                  <span className={styles.feedTime}>
                    {t.project_id ? projectNamesById[t.project_id] : ''}
                    {t.due_date ? ` · ${formatHebrewRelative(t.due_date)}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {tab === 'activity' ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>פעילות אחרונה</h2>
          <ActivityFeed rows={activity} />
        </section>
      ) : null}

      {tab === 'notes' ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>הערות פנימיות</h2>
          {company.notes ? (
            <pre className={styles.notesText}>{company.notes}</pre>
          ) : (
            <p className={styles.notice}>אין הערות · ערוך מתוך "ערוך פרטים".</p>
          )}
        </section>
      ) : null}

      <CompanyDrawer
        open={editOpen}
        company={company}
        orgId={orgId}
        onClose={() => {
          setEditOpen(false);
        }}
      />
    </div>
  );
}
